/**
 * Reminder safety disclaimer state helper (#204).
 *
 * Pure function — no React, no fetch — so we can unit test the
 * decision logic directly. The Portal RemindersSection passes the
 * acknowledged_at timestamp from /me; we return what to render.
 *
 *   stateForDisclaimer(null)        → 'pending'      (State A — full block, form locked)
 *   stateForDisclaimer('2026-…')    → 'acknowledged' (State B — compact note, form open)
 *
 * Loading state is the caller's concern (it can pass undefined while
 * /me is in flight).
 */

export const DISCLAIMER_TEXT = {
  title: '⚠️ Important — please read before setting reminders',
  intro: 'AmigoMio reminder calls are an aid, not a guarantee. Before you use them, please understand:',
  bullets: [
    {
      heading: 'Timing is approximate.',
      body:
        'A reminder set for 09:00 may ring anywhere from 09:00 to 09:10. ' +
        "Don't use this for time-critical alerts.",
    },
    {
      heading: 'Delivery is not guaranteed.',
      body:
        'Calls can fail to reach the user — the tablet might be off, the ' +
        'user might be away, or technical issues may interrupt the call. ' +
        'We retry up to 4 times but cannot guarantee a successful delivery.',
    },
    {
      heading: 'Comprehension is not guaranteed.',
      body:
        'Even if Berta delivers the message, we cannot verify your loved ' +
        'one heard it, understood it, or acted on it.',
    },
    {
      heading: 'Critical reminders need human oversight.',
      body:
        'For medication, medical appointments, or anything where missing ' +
        'the action could harm your loved one, do not rely solely on ' +
        'AmigoMio. A nurse, family member, or in-person check is essential.',
    },
  ],
  closer:
    'AmigoMio reminders supplement, never replace, the people who care ' +
    'for your loved one.',
  // Compact one-line note shown after acknowledgement
  compact:
    'Reminders are an aid only — delivery is approximate (~5–10 min window) ' +
    'and not guaranteed.',
  checkboxLabel: 'I understand and agree.',
  acceptButton: 'Save and continue',
  viewLink: 'View safety notice',
};

/**
 * Resolve which UI state to render based on the user's
 * `reminder_disclaimer_acknowledged_at` value.
 *
 *   undefined → 'loading'      (still fetching /me; render a placeholder)
 *   null / '' → 'pending'      (never acknowledged; State A, form locked)
 *   string    → 'acknowledged' (non-empty; State B; form unlocked)
 *
 * Note: pre-launch backfill (migration 0035) gives every existing
 * family_user a non-null timestamp, so 'pending' is only for accounts
 * created after the migration shipped — i.e. the only ones that
 * actually consented.
 */
export function stateForDisclaimer(acknowledgedAt) {
  if (acknowledgedAt === undefined) return 'loading';
  // #1036 — fail closed: any falsy value (null, or a defensive empty
  // string from a malformed /me payload) counts as not-yet-acknowledged.
  // This gates the reminder SAFETY disclaimer, so only a real timestamp
  // string may unlock the form.
  if (!acknowledgedAt) return 'pending';
  return 'acknowledged';
}
